import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaFilter, 
  FaSort, 
  FaChevronDown, 
  FaSpinner,
  FaExclamationTriangle,
  FaStar,
  FaCheckCircle,
  FaEye
} from 'react-icons/fa';
import RatingDisplay from './RatingDisplay';
import StarRating from './StarRating';
import { useRatings } from '../../hooks/useRatings';
import { SORT_OPTIONS, FILTER_OPTIONS } from '../../utils/ratingConstants';

/**
 * Paginated Ratings List Component
 * Displays restaurant ratings with filtering and sorting
 */
const RatingsList = ({
  restaurantId,
  showFilters = true,
  showPagination = true,
  itemsPerPage = 10,
  showUserActions = true,
  className = ''
}) => {
  const [filters, setFilters] = useState({
    minRating: null,
    verifiedOnly: false,
    sortBy: 'newest'
  });
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [showSortDropdown, setShowSortDropdown] = useState(false);

  const {
    ratings,
    analytics,
    loading,
    error,
    pagination,
    fetchRatings,
    loadMore
  } = useRatings(restaurantId);

  // Apply filters and fetch ratings
  useEffect(() => {
    if (restaurantId) {
      const sortOption = SORT_OPTIONS[filters.sortBy.toUpperCase()] || SORT_OPTIONS.NEWEST;
      
      fetchRatings({
        page: 1,
        limit: itemsPerPage,
        sortBy: sortOption.sortBy,
        sortOrder: sortOption.sortOrder,
        minRating: filters.minRating,
        verifiedOnly: filters.verifiedOnly
      });
    }
  }, [restaurantId, filters, itemsPerPage, fetchRatings]);

  // Handle filter changes
  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
    setShowFilterDropdown(false);
    setShowSortDropdown(false);
  };

  // Handle load more
  const handleLoadMore = () => {
    if (pagination.hasNextPage && !loading) {
      loadMore();
    }
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      minRating: null,
      verifiedOnly: false,
      sortBy: 'newest'
    });
  };

  if (loading && ratings.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <FaSpinner className="animate-spin text-orange-500 text-2xl mr-3" />
        <span className="text-gray-600">Loading ratings...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <FaExclamationTriangle className="text-red-500 text-3xl mx-auto mb-3" />
        <h3 className="text-red-800 font-bold text-lg mb-2">Error Loading Ratings</h3>
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={() => fetchRatings()}
          className="bg-red-500 text-white px-6 py-2 rounded-lg hover:bg-red-600 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header with Analytics Summary */}
      {analytics && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-orange-50 to-red-50 rounded-xl p-6 border border-orange-100"
        >
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="flex items-center justify-center mb-2">
                <StarRating 
                  value={analytics.summary?.averageRating || 0} 
                  readonly 
                  size="lg" 
                  showValue 
                />
              </div>
              <p className="text-sm text-gray-600">Average Rating</p>
            </div>
            
            <div className="text-center">
              <div className="text-3xl font-bold text-gray-800 mb-1">
                {analytics.summary?.totalRatings || 0}
              </div>
              <p className="text-sm text-gray-600">Total Reviews</p>
            </div>
            
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600 mb-1">
                {analytics.engagement?.verifiedPurchaseRate || 0}%
              </div>
              <p className="text-sm text-gray-600">Verified Purchases</p>
            </div>
            
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600 mb-1">
                {analytics.engagement?.reviewRate || 0}%
              </div>
              <p className="text-sm text-gray-600">With Reviews</p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Filters and Sorting */}
      {showFilters && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between flex-wrap gap-4"
        >
          <div className="flex items-center gap-4">
            {/* Filter Dropdown */}
            <div className="relative">
              <button
                onClick={() => {
                  setShowFilterDropdown(!showFilterDropdown);
                  setShowSortDropdown(false);
                }}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <FaFilter className="text-gray-500" />
                <span>Filter</span>
                <FaChevronDown className={`text-gray-500 transition-transform ${showFilterDropdown ? 'rotate-180' : ''}`} />
              </button>

              <AnimatePresence>
                {showFilterDropdown && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute top-full left-0 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-10"
                  >
                    <div className="p-4 space-y-4">
                      {/* Rating Filter */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Minimum Rating
                        </label>
                        <div className="space-y-2">
                          {FILTER_OPTIONS.RATING_LEVELS.map((level) => (
                            <button
                              key={level.value}
                              onClick={() => handleFilterChange('minRating', level.value)}
                              className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                                filters.minRating === level.value
                                  ? 'bg-orange-100 text-orange-800'
                                  : 'hover:bg-gray-50'
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                <StarRating value={level.value} readonly size="sm" />
                                <span className={`text-sm ${level.color}`}>{level.label}</span>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Verified Only Filter */}
                      <div>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={filters.verifiedOnly}
                            onChange={(e) => handleFilterChange('verifiedOnly', e.target.checked)}
                            className="w-4 h-4 text-orange-500 border-gray-300 rounded focus:ring-orange-200"
                          />
                          <FaCheckCircle className="text-green-500 text-sm" />
                          <span className="text-sm text-gray-700">Verified purchases only</span>
                        </label>
                      </div>

                      {/* Clear Filters */}
                      <button
                        onClick={clearFilters}
                        className="w-full px-3 py-2 text-sm text-gray-600 hover:text-gray-800 border-t border-gray-200 pt-3"
                      >
                        Clear all filters
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Sort Dropdown */}
            <div className="relative">
              <button
                onClick={() => {
                  setShowSortDropdown(!showSortDropdown);
                  setShowFilterDropdown(false);
                }}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <FaSort className="text-gray-500" />
                <span>Sort</span>
                <FaChevronDown className={`text-gray-500 transition-transform ${showSortDropdown ? 'rotate-180' : ''}`} />
              </button>

              <AnimatePresence>
                {showSortDropdown && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute top-full left-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-10"
                  >
                    <div className="p-2">
                      {Object.entries(SORT_OPTIONS).map(([key, option]) => (
                        <button
                          key={key}
                          onClick={() => handleFilterChange('sortBy', key.toLowerCase())}
                          className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                            filters.sortBy === key.toLowerCase()
                              ? 'bg-orange-100 text-orange-800'
                              : 'hover:bg-gray-50'
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Active Filters Display */}
          <div className="flex items-center gap-2">
            {filters.minRating && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm">
                <FaStar className="text-xs" />
                {filters.minRating}+ stars
                <button
                  onClick={() => handleFilterChange('minRating', null)}
                  className="ml-1 text-orange-600 hover:text-orange-800"
                >
                  ×
                </button>
              </span>
            )}
            
            {filters.verifiedOnly && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                <FaCheckCircle className="text-xs" />
                Verified only
                <button
                  onClick={() => handleFilterChange('verifiedOnly', false)}
                  className="ml-1 text-green-600 hover:text-green-800"
                >
                  ×
                </button>
              </span>
            )}
          </div>
        </motion.div>
      )}

      {/* Ratings List */}
      <div className="space-y-4">
        {ratings.length === 0 ? (
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-8 text-center">
            <FaEye className="text-gray-400 text-4xl mx-auto mb-4" />
            <h3 className="text-gray-700 font-bold text-lg mb-2">No Reviews Yet</h3>
            <p className="text-gray-500">
              {filters.minRating || filters.verifiedOnly
                ? 'No reviews match your current filters. Try adjusting your criteria.'
                : 'Be the first to share your experience with this restaurant!'
              }
            </p>
            {(filters.minRating || filters.verifiedOnly) && (
              <button
                onClick={clearFilters}
                className="mt-4 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
              >
                Clear Filters
              </button>
            )}
          </div>
        ) : (
          <AnimatePresence>
            {ratings.map((rating, index) => (
              <motion.div
                key={rating._id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <RatingDisplay
                  rating={rating}
                  showActions={showUserActions}
                  showCriteria={true}
                />
              </motion.div>
            ))}
          </AnimatePresence>
        )}
      </div>

      {/* Load More / Pagination */}
      {showPagination && pagination.hasNextPage && (
        <div className="text-center">
          <button
            onClick={handleLoadMore}
            disabled={loading}
            className="flex items-center gap-2 mx-auto px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl hover:from-orange-600 hover:to-red-600 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <FaSpinner className="animate-spin" />
                Loading...
              </>
            ) : (
              <>
                <FaEye />
                Load More Reviews
              </>
            )}
          </button>
          
          <p className="text-sm text-gray-500 mt-2">
            Showing {ratings.length} of {pagination.totalRatings} reviews
          </p>
        </div>
      )}

      {/* Click outside to close dropdowns */}
      {(showFilterDropdown || showSortDropdown) && (
        <div
          className="fixed inset-0 z-5"
          onClick={() => {
            setShowFilterDropdown(false);
            setShowSortDropdown(false);
          }}
        />
      )}
    </div>
  );
};

export default RatingsList;